import { useGoogleMap } from '@react-google-maps/api';
import React, {
  memo,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from 'react';
import { useTheme } from '@mui/material/styles';
import Supercluster from 'supercluster';
import MapQuadrantIcon, { getClusterIconSize } from './MapQuadrantIcon';
import MapPriceIcon, {
  getPriceIconSize,
  getPriceLabelColor,
  PRICE_ICON_LABEL_Y,
} from './MapPriceIcon';
import { getCheapestRate } from '@shared/helpers';

const CLUSTER_CLICK_ZOOM_STEP = 1;
const CLUSTER_CLICK_MAX_ZOOM = 19;
const CLUSTER_RADIUS = 400;
const NO_PRICE_LABEL = 'Consultar';
const TILE_SIZE = 256;

// Arriba de esta cantidad no se corrige el número de la burbuja pidiéndole
// sus miembros al índice: sale caro, y en un número grande no se nota.
const COUNT_FIX_MAX = 100;

// Bandas de zIndex: si una burbuja y un pin se superponen gana la burbuja, y
// lo hovereado va siempre arriba de todo. El índice dentro de cada banda se
// acota para que una lista larga de hoteles no invada la banda de abajo.
const Z_BAND = 1000;
const Z_PRICE = 1000;
const Z_TOP = 2000;
const Z_CLUSTER = 3000;
const Z_HOVERED = 5000;

// Proyección Mercator estándar para calcular el área visible a partir del
// centro, el zoom y el tamaño en píxeles del contenedor.
const lngToX = lng => (lng + 180) / 360;
const latToY = lat => {
  const sinLat = Math.sin((lat * Math.PI) / 180);
  return 0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI);
};
const xToLng = x => x * 360 - 180;
const yToLat = y => {
  const n = Math.PI - 2 * Math.PI * y;
  return (180 / Math.PI) * Math.atan((Math.exp(n) - Math.exp(-n)) / 2);
};

// bbox = [west, south, east, north], mismo formato que devuelve computeBbox
// y que espera Supercluster.
const isHotelWithinBbox = (item, bbox) => {
  if (!bbox) return true;
  const [west, south, east, north] = bbox;
  return (
    item.lat >= south &&
    item.lat <= north &&
    item.lng >= west &&
    item.lng <= east
  );
};

const hasPrice = item =>
  getCheapestRate(item.hotel.rates)?.price?.details_USD?.total != null;

const sortByPrice = (a, b) =>
  getCheapestRate(a.hotel.rates).price.details_USD.total -
  getCheapestRate(b.hotel.rates).price.details_USD.total;

const computeBbox = (center, zoom, widthPx, heightPx) => {
  const worldSize = TILE_SIZE * 2 ** zoom;
  const centerX = lngToX(center.lng());
  const centerY = latToY(center.lat());
  const dx = widthPx / worldSize;
  const dy = heightPx / worldSize;

  return [
    xToLng(centerX - dx / 2),
    yToLat(centerY + dy / 2),
    xToLng(centerX + dx / 2),
    yToLat(centerY - dy / 2),
  ];
};

// AdvancedMarkerElement solo acepta un nodo DOM como `content`: acá se arma
// a mano el pin (SVG) con el precio superpuesto.
const buildMarkerContent = (svgMarkup, width, height, label) => {
  const wrapper = document.createElement('div');
  wrapper.style.position = 'relative';
  wrapper.style.width = `${width}px`;
  wrapper.style.height = `${height}px`;
  wrapper.innerHTML = svgMarkup;

  if (label) {
    const span = document.createElement('span');
    span.textContent = label.text;
    span.style.position = 'absolute';
    span.style.left = '50%';
    span.style.top = `${label.y}px`;
    span.style.transform = 'translate(-50%, -50%)';
    span.style.color = label.color;
    span.style.fontSize = '15px';
    span.style.fontWeight = '700';
    span.style.fontFamily = 'Open Sans, sans-serif';
    span.style.pointerEvents = 'none';
    span.style.whiteSpace = 'nowrap';
    wrapper.appendChild(span);
  }

  return wrapper;
};

const MapMarkers = ({
  hotels = [],
  hoveredHotel,
  setIsLoadImage,
  handleModal,
  getFormattedPrice,
  topMarkersCount = 15,
}) => {
  // Color de marca del sitio actual, para el hover del pin.
  const { tertiary: hoverColor } = useTheme();

  // Instancia viva del mapa vía contexto (no una ref manual, que puede quedar
  // apuntando a un mapa viejo si se remonta, por ejemplo con StrictMode).
  const map = useGoogleMap();

  // Hover propio del mapa, independiente del que llega desde la card de la lista.
  const [hoveredMarkerId, setHoveredMarkerId] = useState(null);

  // Bbox + zoom actuales, recalculados en cada 'idle' del mapa.
  const [view, setView] = useState(null);

  useEffect(() => {
    if (!map) return;

    const updateView = () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      const div = map.getDiv();
      if (!center || zoom == null || !div?.offsetWidth || !div?.offsetHeight) {
        return;
      }

      setView({
        bbox: computeBbox(center, zoom, div.offsetWidth, div.offsetHeight),
        zoom,
      });
    };

    updateView();
    const listener = map.addListener('idle', updateView);
    return () => listener.remove();
  }, [map]);

  const hotelsPositions = useMemo(() => {
    const usedPositions = {};
    return hotels
      .filter(
        h => h?.location?.latitude != null && h?.location?.longitude != null
      )
      .map(h => {
        let lat = Number(h.location.latitude);
        let lng = Number(h.location.longitude);
        const key = `${lat}_${lng}`;
        const samePositionIndex = usedPositions[key] || 0;
        usedPositions[key] = samePositionIndex + 1;
        if (samePositionIndex > 0) {
          const offset = 0.00018; // Separación física en metros
          const angle = (samePositionIndex * Math.PI) / 4;
          lat += offset * Math.cos(angle);
          lng += offset * Math.sin(angle);
        }
        return {
          id: h.id_hotel,
          hotel: h,
          lat,
          lng,
        };
      });
  }, [hotels]);

  // Top N más baratos dentro del área visible. Se recalcula en cada pan/zoom,
  // pero solo filtra y ordena: es barato, a diferencia del índice de abajo.
  const hotelsTop = useMemo(() => {
    const isVisibleWithPrice = item =>
      hasPrice(item) && isHotelWithinBbox(item, view?.bbox);

    return hotelsPositions
      .filter(isVisibleWithPrice)
      .sort(sortByPrice)
      .slice(0, topMarkersCount);
  }, [hotelsPositions, topMarkersCount, view]);

  const topSet = useMemo(() => new Set(hotelsTop), [hotelsTop]);

  // Índice sobre TODOS los hoteles (no solo los que quedan fuera del top):
  // depende únicamente de la lista de hoteles, nunca del viewport.
  // `load()` precalcula el clustering de cada nivel de zoom de una sola vez,
  // así que reconstruirlo en cada pan/zoom trababa el navegador. Los del top
  // se descartan más abajo, al consultar — eso sí es barato.
  const clusterIndex = useMemo(() => {
    const index = new Supercluster({ radius: CLUSTER_RADIUS, maxZoom: 18 });
    index.load(
      hotelsPositions.map(({ lat, lng }, hotelIndex) => ({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: [lng, lat] },
        properties: { hotelIndex },
      }))
    );
    return index;
  }, [hotelsPositions]);

  // A cada burbuja se le descuentan los hoteles que ya se muestran como pin
  // del top, para que el número sea el que realmente aparece al hacer click:
  // si no le queda ninguno no se dibuja, y si le queda uno se dibuja ese
  // hotel con su precio en vez de un "1".
  const clusters = useMemo(() => {
    if (!view) return [];

    const isTopPin = point =>
      topSet.has(hotelsPositions[point.properties.hotelIndex]);
    const isNotTopPin = point => !isTopPin(point);

    const toHotelMarker = point => ({
      isCluster: false,
      hotelIndex: point.properties.hotelIndex,
      lng: point.geometry.coordinates[0],
      lat: point.geometry.coordinates[1],
    });

    const toClusterMarker = (feature, count, isExact) => ({
      isCluster: true,
      clusterId: feature.properties.cluster_id,
      count,
      isExact,
      lng: feature.geometry.coordinates[0],
      lat: feature.geometry.coordinates[1],
    });

    const resolve = feature => {
      if (!feature.properties.cluster) {
        if (isTopPin(feature)) return null;
        return toHotelMarker(feature);
      }

      const { cluster_id, point_count } = feature.properties;
      if (point_count > COUNT_FIX_MAX) {
        return toClusterMarker(feature, point_count, false);
      }

      const hidden = clusterIndex
        .getLeaves(cluster_id, point_count)
        .filter(isNotTopPin);

      if (hidden.length === 0) return null;
      if (hidden.length === 1) return toHotelMarker(hidden[0]);
      return toClusterMarker(feature, hidden.length, true);
    };

    return clusterIndex
      .getClusters(view.bbox, Math.round(view.zoom))
      .map(resolve)
      .filter(Boolean);
  }, [clusterIndex, view, topSet, hotelsPositions]);

  const getZIndex = useCallback((isHovered, band, index) => {
    if (isHovered) return Z_HOVERED;
    return band - Math.min(index, Z_BAND - 1);
  }, []);

  // Zoom de expansión real del cluster (a partir del cual empieza a
  // separarse), con un mínimo garantizado de +1 paso y tope en
  // CLUSTER_CLICK_MAX_ZOOM. Mismo comportamiento de siempre, sin excepciones
  // por tipo de burbuja: lo único que cambia hoy es el número que muestra.
  const handleClusterClick = useCallback(
    (clusterId, lat, lng) => {
      if (!map) return;

      const expansionZoom = clusterIndex.getClusterExpansionZoom(clusterId);
      const targetZoom = Math.min(
        Math.max(expansionZoom, map.getZoom() + CLUSTER_CLICK_ZOOM_STEP),
        CLUSTER_CLICK_MAX_ZOOM
      );

      map.panTo({ lat, lng });
      map.setZoom(targetZoom);
    },
    [clusterIndex, map]
  );

  // Datos para renderizar como AdvancedMarkerElement: top N + agrupamiento,
  // con posición, zIndex, ícono, label, click y hover de cada uno.
  const markerSpecs = useMemo(() => {
    const topSpecs = hotelsTop.map(({ hotel, lat, lng }, idx) => {
      const key = hotel.id_hotel ?? `top-${idx}`;
      const isHovered = hoveredHotel === hotel || hoveredMarkerId === key;
      const isFav = hotel?.id_fav_message != null;
      const priceText = getFormattedPrice({
        price: getCheapestRate(hotel?.rates)?.price,
      });
      const { width, height } = getPriceIconSize(priceText);

      return {
        key: `top-${key}`,
        lat,
        lng,
        zIndex: getZIndex(isHovered, Z_TOP, idx),
        width,
        height,
        svg: MapPriceIcon({ width, isHovered, isFav, hoverColor }),
        label: {
          text: priceText,
          color: getPriceLabelColor(isHovered, isFav, hoverColor),
          y: PRICE_ICON_LABEL_Y,
        },
        hoverKey: key,
        // El click es un evento DOM real y burbujea hasta el onClick del mapa
        // (que cierra el modal); sin esto, abrir y cerrar pasa en el mismo click.
        stopPropagation: true,
        contentKey: `${priceText}|${isHovered}|${isFav}|${hoverColor}`,
        onClick: () => {
          setIsLoadImage(false);
          handleModal(true, hotel);
        },
      };
    });

    const restSpecs = clusters.map(marker => {
      const { lat, lng } = marker;

      if (marker.isCluster) {
        // "+" solo cuando el conteo no está corregido (cluster grande, sin
        // getLeaves por costo): puede haber más ocultos de los que muestra.
        const countLabel = marker.isExact
          ? `${marker.count}`
          : `+${marker.count}`;
        const { width, height } = getClusterIconSize(countLabel);

        return {
          key: `cluster-${marker.clusterId}`,
          lat,
          lng,
          zIndex: Z_CLUSTER,
          width,
          height,
          svg: MapQuadrantIcon({ count: countLabel }),
          label: null,
          hoverKey: null,
          stopPropagation: false,
          contentKey: countLabel,
          onClick: () => handleClusterClick(marker.clusterId, lat, lng),
        };
      }

      const { hotel } = hotelsPositions[marker.hotelIndex];
      const restKey = hotel.id_hotel ?? marker.hotelIndex;
      const isHovered = hoveredHotel === hotel || hoveredMarkerId === restKey;
      const isFav = hotel?.id_fav_message != null;
      const restPriceText =
        getFormattedPrice({ price: getCheapestRate(hotel?.rates)?.price }) ||
        NO_PRICE_LABEL;
      const { width, height } = getPriceIconSize(restPriceText);

      return {
        key: `rest-${hotel.id_hotel ?? marker.hotelIndex}`,
        lat,
        lng,
        zIndex: getZIndex(isHovered, Z_PRICE, marker.hotelIndex),
        width,
        height,
        svg: MapPriceIcon({ width, isHovered, isFav, hoverColor }),
        label: {
          text: restPriceText,
          color: getPriceLabelColor(isHovered, isFav, hoverColor),
          y: PRICE_ICON_LABEL_Y,
        },
        hoverKey: restKey,
        stopPropagation: true,
        contentKey: `${restPriceText}|${isHovered}|${isFav}|${hoverColor}`,
        onClick: () => {
          setIsLoadImage(false);
          handleModal(true, hotel);
        },
      };
    });

    return [...topSpecs, ...restSpecs];
  }, [
    hotelsTop,
    clusters,
    hotelsPositions,
    hoveredHotel,
    hoveredMarkerId,
    hoverColor,
    getFormattedPrice,
    getZIndex,
    handleClusterClick,
    setIsLoadImage,
    handleModal,
  ]);

  // @react-google-maps/api no tiene un componente <AdvancedMarker>: las
  // instancias se crean/actualizan/destruyen acá a mano, una por key de
  // markerSpecs, guardando el `contentKey` con el que se armó su DOM actual.
  const markersByKey = useRef(new Map());

  useEffect(() => {
    if (!map || !window.google?.maps?.marker) return;

    const markers = markersByKey.current;
    const seenKeys = new Set();

    markerSpecs.forEach(spec => {
      seenKeys.add(spec.key);
      let entry = markers.get(spec.key);

      if (!entry) {
        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          map,
          position: { lat: spec.lat, lng: spec.lng },
          zIndex: spec.zIndex,
          content: buildMarkerContent(
            spec.svg,
            spec.width,
            spec.height,
            spec.label
          ),
        });
        entry = { marker, contentKey: spec.contentKey };
        markers.set(spec.key, entry);
      } else {
        entry.marker.position = { lat: spec.lat, lng: spec.lng };
        entry.marker.zIndex = spec.zIndex;

        // Solo se reconstruye el DOM si cambió lo visual de ESTE marker; una
        // posición o zIndex nuevos (o el hover de OTRO marker) no alcanzan.
        if (entry.contentKey !== spec.contentKey) {
          entry.marker.content = buildMarkerContent(
            spec.svg,
            spec.width,
            spec.height,
            spec.label
          );
          entry.contentKey = spec.contentKey;
        }
      }

      entry.marker.content.onclick = e => {
        if (spec.stopPropagation) e.stopPropagation();
        spec.onClick();
      };
      if (spec.hoverKey != null) {
        entry.marker.content.onmouseenter = () =>
          setHoveredMarkerId(spec.hoverKey);
        entry.marker.content.onmouseleave = () => setHoveredMarkerId(null);
      }
    });

    markers.forEach((entry, key) => {
      if (seenKeys.has(key)) return;
      entry.marker.map = null;
      markers.delete(key);
    });
  }, [map, markerSpecs]);

  // Al desmontar (cambio de página, búsqueda nueva) se van todos, sin
  // esperar a que markerSpecs vuelva a correr con la lista vacía.
  useEffect(
    () => () => {
      markersByKey.current.forEach(entry => {
        entry.marker.map = null;
      });
      markersByKey.current.clear();
    },
    []
  );

  return null;
};

export default memo(MapMarkers);
